import { COLLECTIONS } from '../../constants/collections';
import { VEHICLE_MOVEMENT_EVENTS } from '../../constants/systemConfig';
import { createDocument, getDocument, updateDocument, getAllDocuments } from '../firestore/firestoreService';

/**
 * Vehicle Movement Logger - Araç hareket takibi
 */

const getCollectionPath = (tenantId) => `tenants/${tenantId}/${COLLECTIONS.VEHICLE_MOVEMENT_LOGS}`;

// Create new vehicle movement log
export const createVehicleMovementLog = async (reservationId, plateNumber, tenantId) => {
    const data = {
        reservationId,
        plateNumber,
        tenantId,
        movements: [],
        currentStatus: null,
        createdAt: new Date(),
        updatedAt: new Date()
    };

    return await createDocument(getCollectionPath(tenantId), data);
};

// Add movement event to log
export const logVehicleMovement = async (logId, eventData, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required for logging vehicle movement');

    const log = await getDocument(getCollectionPath(tenantId), logId);

    if (!log) {
        throw new Error('Araç hareket logu bulunamadı');
    }

    const movement = {
        eventType: eventData.eventType,
        timestamp: new Date(),
        location: eventData.location || null,
        performedBy: eventData.performedBy || null,
        metadata: eventData.metadata || {}
    };

    const updatedMovements = [...log.movements, movement];

    return await updateDocument(getCollectionPath(tenantId), logId, {
        movements: updatedMovements,
        currentStatus: eventData.eventType,
        updatedAt: new Date()
    });
};

// Get vehicle movement log by reservation ID
export const getVehicleMovementLogByReservation = async (reservationId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required to get movement log');

    const conditions = [
        { type: 'where', field: 'reservationId', operator: '==', value: reservationId }
    ];

    const results = await getAllDocuments(getCollectionPath(tenantId), conditions);
    return results.length > 0 ? results[0] : null;
};

// Get vehicle movement log by ID
export const getVehicleMovementLog = async (logId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required to get movement log');
    return await getDocument(getCollectionPath(tenantId), logId);
};

// Get all vehicle movement logs for a date range
export const getVehicleMovementLogsByDateRange = async (tenantId, startDate, endDate) => {
    if (!tenantId) throw new Error('Tenant ID is required for movement logs');

    const conditions = [
        { type: 'where', field: 'createdAt', operator: '>=', value: startDate },
        { type: 'where', field: 'createdAt', operator: '<=', value: endDate },
        { type: 'orderBy', field: 'createdAt', direction: 'desc' }
    ];

    return await getAllDocuments(getCollectionPath(tenantId), conditions);
};

// Helper: Log facility entry
export const logFacilityEntry = async (reservationId, plateNumber, tenantId, performedBy) => {
    // Create log if doesn't exist, or get existing
    let log = await getVehicleMovementLogByReservation(reservationId, tenantId);

    if (!log) {
        const logId = await createVehicleMovementLog(reservationId, plateNumber, tenantId);
        log = await getDocument(getCollectionPath(tenantId), logId);
    }

    return await logVehicleMovement(log.id, {
        eventType: VEHICLE_MOVEMENT_EVENTS.FACILITY_ENTRY,
        location: 'Ana Giriş',
        performedBy
    }, tenantId);
};

// Helper: Log empty weighing
export const logEmptyWeighing = async (logId, weight, scaleId, performedBy, tenantId) => {
    return await logVehicleMovement(logId, {
        eventType: VEHICLE_MOVEMENT_EVENTS.WEIGH_EMPTY,
        location: `Kantar ${scaleId}`,
        performedBy,
        metadata: { weight, scaleId }
    }, tenantId);
};

// Helper: Log dock arrival
export const logDockArrival = async (logId, dockName, performedBy, tenantId) => {
    return await logVehicleMovement(logId, {
        eventType: VEHICLE_MOVEMENT_EVENTS.DOCK_ARRIVAL,
        location: dockName,
        performedBy
    }, tenantId);
};

// Helper: Log loading start
export const logLoadingStart = async (logId, dockName, performedBy, tenantId) => {
    return await logVehicleMovement(logId, {
        eventType: VEHICLE_MOVEMENT_EVENTS.LOADING_START,
        location: dockName,
        performedBy
    }, tenantId);
};

// Helper: Log loading end
export const logLoadingEnd = async (logId, dockName, performedBy, tenantId) => {
    return await logVehicleMovement(logId, {
        eventType: VEHICLE_MOVEMENT_EVENTS.LOADING_END,
        location: dockName,
        performedBy
    }, tenantId);
};

// Helper: Log full weighing
export const logFullWeighing = async (logId, weight, scaleId, performedBy, tenantId) => {
    return await logVehicleMovement(logId, {
        eventType: VEHICLE_MOVEMENT_EVENTS.WEIGH_FULL,
        location: `Kantar ${scaleId}`,
        performedBy,
        metadata: { weight, scaleId }
    }, tenantId);
};

// Helper: Log facility exit
export const logFacilityExit = async (logId, performedBy, tenantId) => {
    return await logVehicleMovement(logId, {
        eventType: VEHICLE_MOVEMENT_EVENTS.FACILITY_EXIT,
        location: 'Ana Çıkış',
        performedBy
    }, tenantId);
};

